'use client';

import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { ContactFormData } from '../SchedulerContext';
import ContactFormFields from '../components/ContactFormFields';
import { validateSingleEmailAddress } from '@/util/functions/validation/isSingleEmailAddress';

type ContactQuickFormProps = {
  initialEmail?: string;
  onSubmit: (data: ContactFormData) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
};

export default function ContactQuickForm({
  initialEmail = '',
  onSubmit,
  onCancel,
  isLoading = false,
}: ContactQuickFormProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ContactFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: initialEmail,
      phone: '',
    },
    mode: 'onChange',
  });

  // Reset form when initialEmail changes
  useEffect(() => {
    reset({
      firstName: '',
      lastName: '',
      email: initialEmail,
      phone: '',
    });
  }, [initialEmail, reset]);

  return (
    <div className='space-y-3'>
      <div className='rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800'>
        Contact not found. Please enter their information below.
      </div>

      <ContactFormFields
        control={control}
        errors={errors}
        isValid={isValid}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={onCancel}
        cancelLabel='Cancel'
        submitLabel='Add Contact'
        showCancel={true}
        className='space-y-3'
        isLoading={isLoading}
        // Simpler validation rules for quick form
        firstNameRules={{ required: 'First name is required' }}
        lastNameRules={{ required: 'Last name is required' }}
        emailRules={{
          validate: (value: string) =>
            validateSingleEmailAddress(value, { required: true }),
        }}
        phoneRules={{
          required: 'Phone number is required',
          validate: (value: string) => {
            if (!value || !value.trim()) {
              return 'Phone number is required';
            }
            try {
              if (!isValidPhoneNumber(value, 'US')) {
                return 'Please enter a valid phone number';
              }
              return true;
            } catch {
              return 'Please enter a valid phone number';
            }
          },
        }}
      />
    </div>
  );
}
